import type {
  WordPressFieldCheckLevel,
  WordPressFieldCheckMissingItem,
  WordPressFieldCheckResult,
  WordPressFieldCheckSource,
  WordPressPublicationItem,
} from './types';

type ChipColor = 'default' | 'success' | 'warning' | 'error' | 'info';

export const WORDPRESS_FIELD_CHECK_NO_SECTION_LABEL = 'Övriga fält';

export function hasWordPressFieldCheckIssues(result: WordPressFieldCheckResult | null | undefined): boolean {
  return result?.status === 'warnings' || result?.status === 'blocked';
}

export function isWordPressFieldCheckBlocked(result: WordPressFieldCheckResult | null | undefined): boolean {
  return result?.status === 'blocked';
}

export function getWordPressFieldCheckRequiredMissing(result: WordPressFieldCheckResult | null | undefined) {
  return (result?.missing || []).filter(item => item.level === 'required');
}

export function getWordPressFieldCheckWarningMissing(result: WordPressFieldCheckResult | null | undefined) {
  return (result?.missing || []).filter(item => item.level === 'warning');
}

export function getWordPressFieldCheckChip(
  result: WordPressFieldCheckResult | null | undefined,
): { label: string; color: ChipColor } | null {
  if (!result || !hasWordPressFieldCheckIssues(result)) return null;
  if (result.status === 'blocked') {
    return { label: 'Obligatoriskt fält saknas', color: 'error' };
  }
  const count = result.missing.length;
  return { label: `⚠ ${count} fält saknas`, color: 'warning' };
}

export function formatWordPressFieldCheckMissingLabels(missing: WordPressFieldCheckMissingItem[], max = 6): string {
  const labels = missing.map(item => item.label);
  if (labels.length <= max) return labels.join(', ');
  return `${labels.slice(0, max).join(', ')} och ${labels.length - max} till`;
}

export function getWordPressFieldCheckSourceLabel(source: WordPressFieldCheckSource): string | null {
  if (source === 'dabas') return 'Dabas';
  if (source === 'readonly') return 'Skrivskyddat';
  return null;
}

export function getWordPressFieldCheckSourceHint(source: WordPressFieldCheckSource): string | null {
  if (source === 'dabas') return 'Värdet kommer från Dabas och kan inte ändras i Athena. Kan bara ge varning.';
  if (source === 'readonly') return 'Fältet är skrivskyddat i Athena. Kan bara ge varning.';
  return null;
}

export function getWordPressFieldCheckLevelLabel(level: WordPressFieldCheckLevel): string {
  return level === 'required' ? 'Obligatorisk' : 'Varning';
}

export function groupWordPressFieldCheckMissingBySection(missing: WordPressFieldCheckMissingItem[]) {
  const groups = new Map<string, WordPressFieldCheckMissingItem[]>();
  missing.forEach((item) => {
    const section = item.section || WORDPRESS_FIELD_CHECK_NO_SECTION_LABEL;
    groups.set(section, [...(groups.get(section) || []), item]);
  });
  return Array.from(groups.entries()).map(([section, items]) => ({ section, items }));
}

export interface WordPressFieldCheckBulkSummary {
  completeIds: string[];
  warningIds: string[];
  blockedIds: string[];
}

export function summarizeWordPressFieldCheckForItems(items: WordPressPublicationItem[]): WordPressFieldCheckBulkSummary {
  const summary: WordPressFieldCheckBulkSummary = { completeIds: [], warningIds: [], blockedIds: [] };
  items.forEach((item) => {
    const status = item.fieldCheck?.status;
    if (status === 'blocked') {
      summary.blockedIds.push(item.asset._id);
    } else if (status === 'warnings') {
      summary.warningIds.push(item.asset._id);
    } else {
      summary.completeIds.push(item.asset._id);
    }
  });
  return summary;
}
